import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, unref as _unref, createBlock as _createBlock, withCtx as _withCtx, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = ["id"]
const _hoisted_2 = { class: "c-workflow-cmc__inner" }
const _hoisted_3 = {
  key: 0,
  class: "c-workflow-cmc__step-title"
}
const _hoisted_4 = { class: "c-workflow-cmc__icon-ring" }
const _hoisted_5 = ["src", "alt"]
const _hoisted_6 = {
  key: 1,
  class: "c-workflow-cmc__milestone"
}
const _hoisted_7 = {
  key: 1,
  class: "c-workflow-cmc__summary"
}
const _hoisted_8 = {
  key: 2,
  class: "c-workflow-cmc__cta"
}
const _hoisted_9 = {
  key: 0,
  class: "c-workflow-cmc__cta-text"
}
const _hoisted_10 = ["href", "target", "title"]

import { computed } from 'vue';
import { RichText } from '@sitecore-jss/sitecore-jss-vue';
import type {
  WorkflowCMCProps,
  WorkflowCMCSummaryCardFields,
  WorkflowCMCStepFields,
} from '@/types/components/WorkflowCMC';
import type { ScItem } from '@/types/sitecore';
import utils from '@/utils';

type WorkflowCMCStep = ScItem<WorkflowCMCStepFields> & { fields: WorkflowCMCStepFields };
type WorkflowCMCSummaryCard = ScItem<WorkflowCMCSummaryCardFields> & {
  fields: WorkflowCMCSummaryCardFields;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'WorkflowCMC',
  props: {
    dataSource: {},
    fields: {},
    rendering: {},
    params: {}
  },
  setup(__props: any) {

const props = __props;
const fields = computed(() => props.fields);

const rootClass = computed(() => utils.overall.baseComClass(props.fields, 'c-workflow-cmc'));
const rootStyle = computed(() => utils.overall?.baseComStyle?.(fields.value) || {});

const steps = computed<WorkflowCMCStep[]>(() => {
  const items = utils.sc.getFieldObj(fields.value, 'steps');
  return Array.isArray(items)
    ? items.filter((item): item is WorkflowCMCStep => Boolean(item?.fields?.title?.value))
    : [];
});

const summaryCards = computed<WorkflowCMCSummaryCard[]>(() => {
  const items = utils.sc.getFieldObj(fields.value, 'summaryCards');
  return Array.isArray(items)
    ? items.filter((item): item is WorkflowCMCSummaryCard => Boolean(item?.fields?.text?.value))
    : [];
});

const workflowStyle = computed(() => ({
  '--workflow-step-count': `${Math.max(steps.value.length, 1)}`,
}));

const ctaHref = computed(() => fields.value?.ctaLink?.value?.href || '');
const ctaTarget = computed(() => fields.value?.ctaLink?.value?.target || undefined);
const ctaTitle = computed(() => fields.value?.ctaLink?.value?.title || undefined);
const ctaLabel = computed(() => fields.value?.ctaLink?.value?.text || 'Talk to an Expert');

const getIconValue = (step: WorkflowCMCStep) => {
  const icon = step.fields.icon?.value as
    | {
        src?: string;
        alt?: string;
        svgCode?: string;
        value?: {
          src?: string;
          alt?: string;
          svgCode?: string;
        };
      }
    | undefined;

  return icon?.value || icon || {};
};

const getSvgDataUri = (svgCode?: string) => {
  if (!svgCode) {
    return '';
  }

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svgCode)}`;
};

const getIconSrc = (step: WorkflowCMCStep) => {
  const icon = getIconValue(step);
  return icon.src || getSvgDataUri(icon.svgCode);
};
const getIconAlt = (step: WorkflowCMCStep) =>
  getIconValue(step).alt || step.fields.title?.value || '';

const getSummaryCardStyle = (card: WorkflowCMCSummaryCard) => {
  const gridStart = Number(card.fields.gridStart?.value ?? 0);
  const gridSpan = Number(card.fields.gridSpan?.value ?? 0);

  if (!Number.isFinite(gridStart) || gridStart <= 0) {
    return {};
  }

  return {
    '--summary-grid-start': `${gridStart}`,
    '--summary-grid-span': `${Number.isFinite(gridSpan) && gridSpan > 0 ? gridSpan : 2}`,
  };
};

return (_ctx: any,_cache: any) => {
  const _component_JssRichText = _resolveComponent("JssRichText")!
  const _component_ContentGrid = _resolveComponent("ContentGrid")!

  return (fields.value)
    ? (_openBlock(), _createElementBlock("section", {
        key: 0,
        class: _normalizeClass(rootClass.value),
        style: _normalizeStyle(rootStyle.value),
        id: __props.rendering?.dataSource
      }, [
        _createVNode(_component_ContentGrid, {
          "desktop-default-grid": [2, 20],
          "tablet-default-grid": [1, 14],
          "mobile-default-grid": [1, 14]
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_2, [
              _createVNode(_component_JssRichText, {
                class: "c-workflow-cmc__title",
                field: fields.value?.title,
                tag: "h2"
              }, null, 8, ["field"]),
              (steps.value.length)
                ? (_openBlock(), _createElementBlock("div", {
                    key: 0,
                    class: "c-workflow-cmc__workflow",
                    style: _normalizeStyle(workflowStyle.value)
                  }, [
                    _cache[0] || (_cache[0] = _createElementVNode("span", {
                      class: "c-workflow-cmc__timeline-line",
                      "aria-hidden": "true"
                    }, null, -1)),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(steps.value, (step) => {
                      return (_openBlock(), _createElementBlock("article", {
                        key: step.id || step.fields.title?.value,
                        class: "c-workflow-cmc__step"
                      }, [
                        (step.fields.title?.value)
                          ? (_openBlock(), _createElementBlock("h3", _hoisted_3, _toDisplayString(step.fields.title.value), 1))
                          : _createCommentVNode("", true),
                        _createElementVNode("span", _hoisted_4, [
                          (getIconSrc(step))
                            ? (_openBlock(), _createElementBlock("img", {
                                key: 0,
                                class: "c-workflow-cmc__icon",
                                src: getIconSrc(step),
                                alt: getIconAlt(step),
                                loading: "lazy"
                              }, null, 8, _hoisted_5))
                            : _createCommentVNode("", true)
                        ]),
                        (step.fields.milestoneLabel?.value)
                          ? (_openBlock(), _createElementBlock("span", _hoisted_6, _toDisplayString(step.fields.milestoneLabel.value), 1))
                          : _createCommentVNode("", true)
                      ]))
                    }), 128))
                  ], 4))
                : _createCommentVNode("", true),
              (summaryCards.value.length)
                ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(summaryCards.value, (card) => {
                      return (_openBlock(), _createElementBlock("article", {
                        key: card.id || card.fields.text?.value,
                        class: "c-workflow-cmc__summary-card",
                        style: _normalizeStyle(getSummaryCardStyle(card))
                      }, _toDisplayString(card.fields.text.value), 5))
                    }), 128))
                  ]))
                : _createCommentVNode("", true),
              (fields.value.ctaText?.value || ctaHref.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                    (fields.value.ctaText?.value)
                      ? (_openBlock(), _createElementBlock("p", _hoisted_9, _toDisplayString(fields.value.ctaText.value), 1))
                      : _createCommentVNode("", true),
                    (ctaHref.value)
                      ? (_openBlock(), _createElementBlock("a", {
                          key: 1,
                          class: "c-workflow-cmc__cta-link",
                          href: ctaHref.value,
                          target: ctaTarget.value,
                          title: ctaTitle.value
                        }, _toDisplayString(ctaLabel.value), 9, _hoisted_10))
                      : _createCommentVNode("", true)
                  ]))
                : _createCommentVNode("", true),
              (fields.value.footnote?.value)
                ? (_openBlock(), _createBlock(_unref(RichText), {
                    key: 3,
                    class: "c-workflow-cmc__footnote",
                    field: fields.value.footnote
                  }, null, 8, ["field"]))
                : _createCommentVNode("", true)
            ])
          ]),
          _: 1
        })
      ], 14, _hoisted_1))
    : _createCommentVNode("", true)
}
}

})